import { MoreHorizontal } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { SchemaAssignedProvider } from "@/lib/schemaBuilderHelpers";
import type { SchemaSummary } from "@/types/schemaBuilder";

interface SchemaCardProps {
  schema: SchemaSummary;
  onEdit: () => void;
  onDuplicate: () => void;
  onPublish: () => void;
  onArchive: () => void;
  onDelete: () => void;
  canWrite?: boolean; // schema.write — edit, duplicate
  canPublish?: boolean; // schema.publish — promote draft
  canArchive?: boolean; // schema.archive — soft-delete
  canDelete?: boolean; // schema.delete — permanently delete a draft
  pending?: boolean; // optimistic placeholder — disable all actions
  pendingLabel?: string; // badge text shown while pending
  /** PSA providers to show as secondary pills (upload entitlement). */
  assignedProviders?: SchemaAssignedProvider[];
}

// Full class names must appear literally for Tailwind's scanner to pick them up.
const PENDING_STYLES = {
  red: { accent: "border-t-[6px] border-t-red-500", badge: "border-red-500 text-red-700" },
  blue: { accent: "border-t-[6px] border-t-blue-500", badge: "border-blue-500 text-blue-700" },
  yellow: {
    accent: "border-t-[6px] border-t-yellow-400",
    badge: "border-yellow-500 text-yellow-700",
  },
} as const;

export function SchemaCard({
  schema,
  onEdit,
  onDuplicate,
  onPublish,
  onArchive,
  onDelete,
  canWrite = true,
  canPublish = true,
  canArchive = true,
  canDelete = true,
  pending = false,
  pendingLabel = "Creating…",
  assignedProviders = [],
}: SchemaCardProps) {
  const isDraft = schema.status === "draft";
  const isPreset = schema.status === "preset";
  const isPublished = schema.status === "published";
  const isArchived = schema.status === "archived";
  const isDisabled = pending;

  const pendingTone = pendingLabel.startsWith("Deleting")
    ? "red"
    : pendingLabel.startsWith("Publishing")
      ? "blue"
      : "yellow";

  const accentClass = pending
    ? PENDING_STYLES[pendingTone].accent
    : isPreset
      ? "border-t-[6px] border-t-green-500"
      : isDraft
        ? "border-t-[6px] border-t-yellow-400"
        : isPublished
          ? "border-t-[6px] border-t-cyan-500"
          : "border-t-[6px] border-t-gray-500";

  const cardBackgroundClass = isArchived ? "bg-gray-100" : "bg-white";

  const titleClass = isArchived ? "text-gray-500" : "text-[var(--color-text-primary)]";

  const textClass = isArchived ? "text-gray-400" : "text-[var(--color-text-secondary)]";

  const sectionTitleClass = isArchived ? "text-gray-500" : "text-[var(--color-text-primary)]";

  const dividerClass = isArchived ? "border-gray-300" : "border-[var(--color-border-light)]";

  const statusBadge = isDraft
    ? "Draft"
    : isPreset
      ? "Preset"
      : isPublished
        ? "Published"
        : "Archived";

  const statusBadgeClass = isDraft
    ? "border-yellow-500 text-yellow-700"
    : isPreset
      ? "border-green-500 text-green-700"
      : isPublished
        ? "border-cyan-500 text-cyan-700"
        : "border-gray-500 text-gray-600";

  const pendingBadgeClass = PENDING_STYLES[pendingTone].badge;

  return (
    <div
      className={`rounded-md border border-[var(--color-border-light)] p-4 shadow-md ${accentClass} ${cardBackgroundClass} ${pending ? "opacity-60" : ""}`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h3
            title={schema.description ? `${schema.name}\n\n${schema.description}` : schema.name}
            className={`truncate text-base font-semibold ${titleClass}`}
          >
            {schema.name}
          </h3>

          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${pending ? pendingBadgeClass : statusBadgeClass}`}
          >
            {pending ? pendingLabel : statusBadge}
          </span>

          {!pending &&
            assignedProviders.map((provider) => (
              <span
                key={provider.providerId}
                title="This schema is available for upload for this provider"
                className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-text-secondary)]"
              >
                {provider.shortCode}
              </span>
            ))}

          {!pending && isDraft && (schema.conflicts ?? 0) > 0 && (
            <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-medium text-white">
              Conflicts
            </span>
          )}
        </div>

        {(canWrite || canPublish || canArchive || (isDraft && canDelete)) && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild disabled={isDisabled}>
              <button
                disabled={isDisabled}
                className={`-mr-1 rounded p-1 ${
                  isDisabled
                    ? "cursor-not-allowed text-gray-300"
                    : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]"
                }`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                sideOffset={6}
                align="end"
                className="z-50 w-[160px] rounded-md border border-[var(--color-border-light)] bg-white p-2 shadow-xl"
              >
                {isDraft && canWrite && (
                  <DropdownMenu.Item
                    className="cursor-pointer rounded px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none data-[highlighted]:bg-green-100 data-[highlighted]:text-[var(--color-brand-primary)]"
                    onSelect={onEdit}
                  >
                    Edit
                  </DropdownMenu.Item>
                )}

                {canWrite && (
                  <DropdownMenu.Item
                    className="cursor-pointer rounded px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none data-[highlighted]:bg-green-100 data-[highlighted]:text-[var(--color-brand-primary)]"
                    onSelect={onDuplicate}
                  >
                    Duplicate
                  </DropdownMenu.Item>
                )}

                {isDraft && canPublish && (
                  <DropdownMenu.Item
                    className="cursor-pointer rounded px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none data-[highlighted]:bg-green-100 data-[highlighted]:text-[var(--color-brand-primary)]"
                    onSelect={onPublish}
                  >
                    Publish
                  </DropdownMenu.Item>
                )}

                {!isArchived && !isPreset && canArchive && (
                  <DropdownMenu.Item
                    className="cursor-pointer rounded px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none data-[highlighted]:bg-[var(--color-brand-primary)] data-[highlighted]:text-white"
                    onSelect={onArchive}
                  >
                    Archive
                  </DropdownMenu.Item>
                )}

                {isDraft && canDelete && (
                  <DropdownMenu.Item
                    className="cursor-pointer rounded px-3 py-2 text-sm text-red-600 outline-none data-[highlighted]:bg-red-50 data-[highlighted]:text-red-700"
                    onSelect={onDelete}
                  >
                    Delete
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>

      <p className={`mb-4 min-h-[48px] text-sm leading-6 ${textClass}`}>{schema.description}</p>

      <div className={`mb-4 border-t pt-4 ${dividerClass}`}>
        <div className={`mb-3 text-sm font-semibold ${sectionTitleClass}`}>Structure</div>

        <div className={`space-y-2 text-xs ${textClass}`}>
          <div className="flex justify-between">
            <span>Total Columns</span>
            <span>{schema.totalColumns}</span>
          </div>

          <div className="flex justify-between">
            <span>Rules</span>
            <span>{schema.rules}</span>
          </div>

          {isDraft && (
            <div className="flex justify-between">
              <span className={(schema.conflicts ?? 0) > 0 ? "text-red-500" : ""}>Conflicts</span>

              <span className={(schema.conflicts ?? 0) > 0 ? "text-red-500" : ""}>
                {(schema.conflicts ?? 0) > 0 ? schema.conflicts : "None"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className={`mb-3 text-sm font-semibold ${sectionTitleClass}`}>Activity</div>

        <div className={`space-y-2 text-xs ${textClass}`}>
          {isDraft ? (
            <>
              <div className="flex justify-between">
                <span>Last Edited</span>
                <span>{schema.lastEditedAt}</span>
              </div>

              <div className="flex justify-between">
                <span>Last Editor</span>
                <span>{schema.lastEditor || "—"}</span>
              </div>

              <div className="flex justify-between">
                <span>Created</span>
                <span>{schema.createdAt}</span>
              </div>
            </>
          ) : isPreset ? (
            <>
              <div className="flex justify-between">
                <span>Files Re-Mapped</span>
                <span>{schema.filesRemapped ?? 0}</span>
              </div>

              <div className="flex justify-between">
                <span>Last Used</span>
                <span>{schema.lastUsedAt}</span>
              </div>

              <div className="flex justify-between">
                <span>Last Updated</span>
                <span>{schema.lastUpdatedAt}</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <span>Files Re-Mapped</span>
                <span>{schema.filesRemapped ?? 0}</span>
              </div>

              <div className="flex justify-between">
                <span>Last Used</span>
                <span>{schema.lastUsedAt}</span>
              </div>

              <div className="flex justify-between">
                <span>Published</span>
                <span>{schema.publishedAt}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
